import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWallet, 
  faCreditCard, 
  faExchangeAlt, 
  faChartLine, 
  faShieldAlt, 
  faCode,
  faGlobe,
  faLock,
  faCoins,
  faEthernet,
  faDollarSign,
  faEuroSign,
  faPoundSign,
  faMobileScreen,
  faQrcode
} from '@fortawesome/free-solid-svg-icons';
import Lottie from 'react-lottie';
import animationData from '../assets/payment-animation.json';
import unipayQR from '../assets/unipay_qr.png';

const PaymentFlow = () => {
  const containerRef = useRef(null);
  
  // Animation options for Lottie
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };
  
  return (
    <div className="w-full h-full relative flex items-center justify-center" ref={containerRef}>
      {/* Background blobs */}
      <div className="absolute w-64 h-64 left-0 top-0 blob bg-blue-100"></div>
      <div className="absolute w-48 h-48 right-0 bottom-0 blob bg-green-100" style={{ animationDelay: '2s' }}></div>
      
      {/* Main container */}
      <div className="relative z-10 w-full max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Left side - Web3 */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 border border-blue-100">
                <FontAwesomeIcon icon={faWallet} className="text-primary text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Web3 Payments</h3>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#F7931A] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faCoins} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">Bitcoin</span>
                </div>
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#627EEA] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faEthernet} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">Ethereum</span>
                </div>
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#8247E5] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faEthernet} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">Polygon</span>
                </div>
              </div>
            </motion.div>
            
            {/* Middle - Animation */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="w-full md:w-auto relative"
            >
              <div className="relative w-48 h-48 mx-auto">
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/10 to-secondary/10 animate-pulse-slow"></div>
                <div className="relative z-10 flex items-center justify-center h-full">
                  <img 
                    src={unipayQR} 
                    alt="UniPay QR Code" 
                    className="w-36 h-36 object-contain"
                  />
                </div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none">
                  <div className="mt-2 text-xs text-primary/50 font-medium">Scan to Pay</div>
                </div>
              </div>
              <div className="text-center mt-2">
                <span className="text-sm text-gray-500 flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faMobileScreen} className="text-primary" />
                  Scan QR to Pay
                </span>
              </div>
            </motion.div>
            
            {/* Right side - Web2 */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 border border-green-100">
                <FontAwesomeIcon icon={faCreditCard} className="text-secondary text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Fiat Payments</h3>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#85bb65] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faDollarSign} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">USD</span>
                </div>
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#0066cc] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faEuroSign} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">EUR</span>
                </div>
                <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-[#333333] flex items-center justify-center text-white">
                    <FontAwesomeIcon icon={faPoundSign} className="text-xs" />
                  </div>
                  <span className="text-sm text-gray-700">GBP</span>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Bottom status */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-8 bg-gray-50 p-4 rounded-lg border border-gray-100 flex items-center justify-between"
          >
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">System Operational</span>
            </div>
            <div className="text-sm text-gray-500">
              Processing 1000+ transactions/min
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFlow;
